import React from 'react';
import { useId } from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import { ActivityFeedWhammyDispatchContext } from '../ActivityFeedContext';
import { Modal } from '../../../atoms/Modal';
import { Button } from '../../../atoms/Buttons';
import * as AddCommentModalStyled from './AddCommentModal.styled';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Modal for deleting a comment or reply.
 *
 * * At the moment, the "delete comment" and "delete reply" modals are behaviorly identical and
 * * didn't warrant creating two copies of the modal. Let's revisit if behavior starts to diverge.
 */
export const DeleteCommentModal = ({
  renderTrigger,
  feedItemId,
  parentFeedItemId,
  feedItemType
}) => {
  const {
    t
  } = useTranslation('blocks.activityFeed');
  const whammyDispatch = React.useContext(ActivityFeedWhammyDispatchContext);
  if (!whammyDispatch) {
    throw new Error('whammyDispatch must be set');
  }
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [systemError, setSystemError] = React.useState('');
  const ariaLabelledById = useId();

  // Flag for adjusting text depending on whether the modal is for a comment or reply
  const isReplyModal = feedItemType === 'reply' && typeof parentFeedItemId !== 'undefined';
  const handleSubmit = async ev => {
    ev.preventDefault();
    let payload = {
      type: 'START_DELETE_USER_COMMENT',
      feedItemId
    };
    if (isReplyModal) {
      payload = {
        type: 'START_DELETE_REPLY',
        replyId: feedItemId,
        parentFeedItemId
      };
    }
    try {
      setIsSubmitting(true);
      await whammyDispatch(payload);
      setIsOpen(false);
    } catch (err) {
      setSystemError(isReplyModal ? t('DeleteCommentModal.errorDeletingReply', 'There was a problem deleting the reply. Please refresh the page and try again.') : t('DeleteCommentModal.errorDeletingComment', 'There was a problem deleting the comment. Please refresh the page and try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  return /*#__PURE__*/_jsxs(Modal, {
    isOpen: isOpen,
    setIsOpen: setIsOpen,
    closeButtonLabel: t('DeleteCommentModal.closeButtonLabel', 'Close'),
    ariaLabelledById: ariaLabelledById,
    renderTrigger: renderTrigger,
    children: [/*#__PURE__*/_jsx(Modal.Header, {
      children: /*#__PURE__*/_jsx(Modal.Title, {
        id: ariaLabelledById,
        children: isReplyModal ? t('DeleteCommentModal.titleReply', 'Delete reply?') : t('DeleteCommentModal.titleComment', 'Delete comment?')
      })
    }), /*#__PURE__*/_jsxs(Modal.Content, {
      children: [isReplyModal ? t('DeleteCommentModal.permanentReplyDelete', 'This will permanently delete the reply.') : t('DeleteCommentModal.permanentCommentDelete', 'This will permanently delete the comment and all of its replies.'), systemError !== '' && /*#__PURE__*/_jsx(AddCommentModalStyled.SystemError, {
        children: systemError
      })]
    }), /*#__PURE__*/_jsx(Modal.Footer, {
      children: /*#__PURE__*/_jsxs(Modal.CtaButtonGroup, {
        children: [/*#__PURE__*/_jsx(Button.TertiaryDestructive, {
          "data-id": "cp-activity-feed-confirm-delete",
          type: "button",
          onClick: handleSubmit,
          isLoading: isSubmitting,
          children: t('DeleteCommentModal.deleteButton', 'Delete')
        }), /*#__PURE__*/_jsx(Button.TertiaryNeutral, {
          type: "button",
          onClick: () => setIsOpen(false),
          children: t('DeleteCommentModal.cancelButton', 'Cancel')
        })]
      })
    })]
  });
};